/**
 * RWBR — EZRA DRIVER TRAITS (EZRA-S2 §4.0, pp.22-23).
 *
 * The DS flag this project shipped first was too narrow, and the source says so
 * plainly. DS is not a boolean bolted to a card — it is ONE OF EIGHT traits, and
 * EZRA prints two hard constraints next to the list:
 *
 *   "Driver Traits are special skills that allow a driver to distinguish
 *    themselves from other drivers by having a unique ability for in-game use."
 *
 *   "No driver can have more than one (1) trait."
 *   "1st year Rookie drivers will not have any traits."
 *
 * The first of those is why this module replaces `ds?: boolean` with a single
 * `trait?: TraitCode` on the card. Eight booleans would permit a driver who is
 * simultaneously a Drafting Specialist and an Engineer, which EZRA forbids in so
 * many words; one nullable code makes that state unrepresentable rather than
 * merely discouraged.
 *
 * Traits are printed on the EZRA card: "The Red AW in the top right corner is a
 * Driver Trait."
 *
 * ── WHAT IS NOT PUBLISHED ──────────────────────────────────────────────────
 *
 * EZRA defines every trait's EFFECT and never says HOW MANY drivers carry one,
 * or how a trait is chosen. There is no table. That gap is filled here by an
 * explicit house ruling (D-EZ-3, below) rather than by an invented chart, and
 * the share is a parameter so the ruling can be overturned without a code change
 * — or discarded entirely the moment real EZRA cards arrive, since a printed
 * card supersedes a generated one (D-021).
 *
 * ── ONLY IMPLEMENTED TRAITS ARE EVER ASSIGNED ──────────────────────────────
 *
 * `implemented: false` traits are defined here in full, with their verbatim
 * text, so the list is complete and auditable — but `assignTraits` will not hand
 * one out, and a test asserts it. A card must never advertise an ability the
 * engine does not honour: a driver showing RC who gets no road-course trophy
 * upgrade is worse than a driver showing nothing, because the card lies.
 */

import type { DriverCard } from './types.ts';
import { makeRng } from './dice.ts';

export const CITE_TRAITS = 'EZRA-S2 §4.0, pp.22-23 — "Driver Traits"';
export const CITE_ONE_TRAIT = `${CITE_TRAITS} — "No driver can have more than one (1) trait."`;
export const CITE_NO_ROOKIE_TRAITS = `${CITE_TRAITS} — "1st year Rookie drivers will not have any traits."`;
export const CITE_FN_UNIQUE = `${CITE_TRAITS} — "Only one (1) driver will be awarded this trait."`;

/** The eight codes EZRA prints, in the order it prints them. */
export type TraitCode = 'AW' | 'DS' | 'EN' | 'FN' | 'RC' | 'RS' | 'SS' | 'ST';

export interface TraitDefinition {
  code: TraitCode;
  /** As EZRA names it. */
  name: string;
  /** EZRA's own words, so the UI never has to paraphrase the rule. */
  text: string;
  /** Where in the engine it bites — or would. */
  touches: string;
  /**
   * Whether the engine actually honours it TODAY. Only `true` traits are ever
   * assigned to a card; see the module header.
   */
  implemented: boolean;
  /** EZRA awards this to at most one driver in the league. */
  unique: boolean;
  /** Why it is not implemented yet. Empty when it is. */
  blockedBy: string;
}

export const TRAITS: readonly TraitDefinition[] = [
  {
    code: 'AW',
    name: 'Awareness',
    text:
      'Awareness allows the driver who has failed a Trouble Escape roll to roll a 1d6 on a result ' +
      'of 6 his awareness allows the Trouble to be avoided.',
    touches: 'trouble.ts — a second chance after a failed ESCAPE check',
    implemented: true,
    unique: false,
    blockedBy: '',
  },
  {
    code: 'DS',
    name: 'Drafting Specialist',
    text:
      'The DS driver receives a -1 to his gas roll on a Super Speedway Quality Challenges. If ' +
      'successful, he implements a Draft Event (DE) pursuant to the EZRA Draft Rule.',
    touches: 'drafting.ts + draftPhase.ts — the whole super-speedway draft phase',
    implemented: true,
    unique: false,
    blockedBy: '',
  },
  {
    code: 'EN',
    name: 'Engineer',
    text:
      'An Engineer can identify potential problems and in some cases avoid or work around them. ' +
      'Any time this driver is faced with a problem roll a d6. 1-3 the problem exists handle per ' +
      'normal rules, 4-6 the problem is avoided.',
    touches: 'problem.ts — a d6 gate before the PROBLEM row is applied',
    implemented: true,
    unique: false,
    blockedBy: '',
  },
  {
    code: 'FN',
    name: 'Finisher',
    text:
      'In races that are 30 turns or more, a Finisher receives a bonus Performance chip with five ' +
      '(5) turns remaining in the race. Only one (1) driver will be awarded this trait.',
    touches:
      'turn.ts — `traitChipAwards` pays at turn (N-4) of a race of 30+ turns; chips.ts scopes the ' +
      'chip to the MIDDLE/TOP groups (D-EZ-26)',
    implemented: true,
    unique: true,
    blockedBy: '',
  },
  {
    code: 'RC',
    name: 'Road Course Expert',
    text:
      'Road Course Expert gets an automatic Trophy upgrade of 1 level when qualifying on a Road ' +
      'Course. If the driver already has a Gold Trophy and Race Week Events allow him an A ' +
      'placement if the Pole is not open the driver will be placed second on the grid any ' +
      'previously placed drivers superseded by this placement will automatically move back one ' +
      'position.',
    touches:
      'raceWeek.ts — the trophy upgrade during qualifying (D-EZ-24) and the gold-trophy ' +
      'second-on-the-grid branch (D-EZ-25)',
    implemented: true,
    unique: false,
    blockedBy: '',
  },
  {
    code: 'RS',
    name: 'Re-Start Expert',
    text:
      'Re-Start Expert - Any time a race restarts after a Yellow Flag, this driver gets an extra ' +
      'event at the start of the turn if the driver is in the group identified by the race cam ' +
      'dice. Before the Normal turn events this driver has the option to duel anybody within the ' +
      'same group. If this driver wins the Restart Duel the driver will still be eligible for the ' +
      'normal turn processing. If the driver fails the duel that completes their turn. If the ' +
      'driver passes on the duel continue to normal turn processing. If the RS driver is ' +
      'individually designated on the Race Start Result Table during an RSE, the negative RSE ' +
      'result is ignored and the RSE ends.',
    touches:
      'restartEvent.ts + turn.ts — the RSE override (D-EZ-22) and the extra pre-turn restart ' +
      'duel (D-EZ-23)',
    implemented: true,
    unique: false,
    blockedBy: '',
  },
  {
    code: 'SS',
    name: 'Super Speedway Expert',
    text:
      'Super Speedway Experts are assigned a +1 Escape bonus on Super Speedways only and only for ' +
      'Escape checks related to Accidents, Crashes and Wrecks.',
    touches: 'trouble.ts — +1 ESCAPE, super-speedways only',
    implemented: true,
    unique: false,
    blockedBy: '',
  },
  {
    code: 'ST',
    name: 'Short Track Expert',
    text:
      'Short Track Expert - At the start of the 1st turn after the mid-point of a Short Track race ' +
      'this driver receives a bonus Performance chip.',
    touches:
      'turn.ts — `traitChipAwards` pays at the first turn past the mid-point of a Short Track race; ' +
      'chips.ts scopes the chip to the MIDDLE/TOP groups (D-EZ-26)',
    implemented: true,
    unique: false,
    blockedBy: '',
  },
];

export const TRAITS_BY_CODE: Readonly<Record<TraitCode, TraitDefinition>> = Object.freeze(
  Object.fromEntries(TRAITS.map((t) => [t.code, t])) as Record<TraitCode, TraitDefinition>,
);

export const TRAIT_CODES: readonly TraitCode[] = TRAITS.map((t) => t.code);

/** The only traits `assignTraits` will hand out. */
export const IMPLEMENTED_TRAITS: readonly TraitCode[] = TRAITS.filter((t) => t.implemented).map(
  (t) => t.code,
);

export const traitOf = (card: Pick<DriverCard, 'trait'>): TraitDefinition | null =>
  card.trait ? (TRAITS_BY_CODE[card.trait] ?? null) : null;

export const hasTrait = (card: Pick<DriverCard, 'trait'>, code: TraitCode): boolean =>
  card.trait === code;

/** `[DS]`, for the corner of a card. */
export const traitBadge = (card: Pick<DriverCard, 'trait'>): string =>
  card.trait ? `[${card.trait}]` : '';

// ---------------------------------------------------------------------------
// D-EZ-3 — how many drivers carry a trait
// ---------------------------------------------------------------------------

/**
 * The share of eligible drivers who carry a trait. NOT a published number.
 *
 * WHY THIS VALUE. EZRA gives no frequency, so the only quantitative evidence in
 * the whole corpus about how much drafting ought to happen comes from the OTHER
 * drafting sheet by the same two authors — `RWBR_MH_drafting.pdf` (Haley &
 * Peckham, v3, 2016), which states its own design target out loud:
 *
 *   "approximately 17% of the Super Speedway turns … about 5 to 7 turns"
 *   of a 40-50 turn race.
 *
 * That sheet is a MUTUALLY EXCLUSIVE system and D-EZ-1 rejects it as a rule.
 * It is used here only as EVIDENCE OF INTENT — the same authors, writing about
 * the same phenomenon, saying how often they wanted to see a draft. Measured
 * against the implemented EZRA system, a DS population of about one car in
 * twenty lands the draft rate in that neighbourhood; the eight traits are then
 * dealt evenly, so the trait share is that DS share times the number of
 * implemented traits.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  RATIFIED BY HOWARD, 16 Aug 2026. D-EZ-3 is CLOSED at 0.20.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * It was carried as an open ruling for two sessions. It is now a decision, and
 * the reason it survived scrutiny is that **0.20 is the only measured row that
 * satisfies BOTH halves of the printed anchor at once**:
 *
 *   | share | DS starters/race | drafts fire on | turns per race |
 *   |-------|------------------|----------------|----------------|
 *   | 0.15  | 2.0              | 11.3%          | ~5.7           |
 *   | 0.20  | 2.7              | 14.7%          | ~7.4           |  ← both halves
 *   | 0.25  | 3.2              | 18.6%          | ~9.4           |
 *
 * 0.25 lands nearer the stated "17% of turns" on its own, and **overshoots the
 * stated "5 to 7 turns"**. 0.15 satisfies the turn count and undershoots the
 * rate. Only 0.20 is inside both, which is what makes it a calibration rather
 * than a preference.
 *
 * On a 53-driver roster: **11 trait holders, 3 of them Drafting Specialists.**
 *
 * IT REMAINS A HOUSE NUMBER AND IS STILL LABELLED ONE. Ratifying it settles what
 * this league runs on; it does not turn an absent EZRA table into a present one.
 * `traitShare` is still a parameter on `buildField` and `runOffseason`, and
 * `traitShare: 0` still gives a plain PLAAY league. **Real EZRA cards supersede
 * it outright (D-021)** — if the mailed cards show a different proportion, that
 * count wins and this ruling is deleted rather than adjusted.
 */
export const DEFAULT_TRAIT_SHARE = 0.2;

export const CITE_TRAIT_SHARE =
  'D-EZ-3 (RATIFIED 16 Aug 2026, house ruling) — EZRA publishes no trait frequency. 0.20 is the ' +
  'only measured share satisfying both halves of the only quantitative anchor in the corpus: the ' +
  '"approximately 17% of the Super Speedway turns … about 5 to 7 turns" target stated on ' +
  'RWBR_MH_drafting.pdf (Haley & Peckham, v3, 2016), used as evidence of the authors\' intent only, ' +
  'never as a rule (D-EZ-1). 0.25 overshoots the turn count; 0.15 undershoots the rate. Printed ' +
  'cards supersede this outright (D-021).';

export interface TraitAssignmentOptions {
  /** Fraction of eligible drivers who get a trait. Default `DEFAULT_TRAIT_SHARE`. */
  share?: number;
  /**
   * Which traits may be dealt. Defaults to the implemented ones. Narrow it to
   * pin a field to a single trait for a test or a scenario.
   */
  pool?: readonly TraitCode[];
  /**
   * Driver ids that are first-year rookies and must get nothing
   * (`CITE_NO_ROOKIE_TRAITS`).
   */
  rookieIds?: Iterable<string>;
  /**
   * Traits already held elsewhere in the league. Only matters for unique traits
   * (FN): if FN is already out there, this deal will not produce a second one.
   */
  alreadyHeld?: Iterable<TraitCode>;
}

export interface TraitAssignment {
  /** driverId → trait. Drivers absent from the map carry none. */
  byDriver: Map<string, TraitCode>;
  /** How many eligible drivers there were to deal to. */
  eligible: number;
  /** Rookies passed over. */
  rookiesSkipped: number;
  share: number;
  ruleCitation: string;
}

/**
 * Deal traits across a roster, deterministically.
 *
 * A SEPARATE RANDOM STREAM, like the garage. `field.ts` already keeps identity,
 * card rolls and chartering on three streams for one reason: changing one must
 * not repaint another. Traits are the fourth. If a trait were rolled inside
 * `createDriver`, every existing seed would produce a different field — every
 * rating, every name, every saved career — because one extra die anywhere in the
 * chain shifts everything after it. Dealing them afterwards, off their own
 * stream, means turning traits on changes exactly one thing: who has a trait.
 *
 * EXACT COUNT, NOT A COIN FLIP PER DRIVER. `round(share × eligible)` drivers are
 * chosen from a shuffle. Independent per-driver probability would let the DS
 * population swing from zero to a dozen between seeds, and the draft rate scales
 * with it — one season would have no drafting and the next would be nothing but.
 * An exact count makes the draft rate a property of the ruleset instead of a
 * property of the seed.
 */
export function assignTraits(
  seed: number,
  drivers: readonly Pick<DriverCard, 'driverId'>[],
  options: TraitAssignmentOptions = {},
): TraitAssignment {
  const share = options.share ?? DEFAULT_TRAIT_SHARE;
  if (!(share >= 0 && share <= 1)) {
    throw new RangeError(`trait share must be between 0 and 1, got ${share}`);
  }

  const pool = (options.pool ?? IMPLEMENTED_TRAITS).filter((code) => TRAITS_BY_CODE[code]);
  const rookies = new Set(options.rookieIds ?? []);
  const held = new Set(options.alreadyHeld ?? []);

  const byDriver = new Map<string, TraitCode>();
  const eligibleDrivers = drivers.filter((d) => !rookies.has(d.driverId));
  const result: TraitAssignment = {
    byDriver,
    eligible: eligibleDrivers.length,
    rookiesSkipped: drivers.length - eligibleDrivers.length,
    share,
    ruleCitation: `${CITE_ONE_TRAIT} ${CITE_NO_ROOKIE_TRAITS} ${CITE_TRAIT_SHARE}`,
  };
  if (pool.length === 0 || eligibleDrivers.length === 0) return result;

  // The fourth stream. Salted differently from identity (0x9e3779b9) and the
  // garage (0x85ebca6b) so no two streams can ever run in lockstep.
  const rng = makeRng((seed ^ 0xc2b2ae35) >>> 0);

  const order = eligibleDrivers.map((d) => d.driverId);
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }

  // Build the exact multiset of traits this league should hold, then shuffle it
  // over the shuffled drivers. Going through `spreadEvenly` rather than dealing
  // round-robin off a shuffled deck is what makes an initial deal and a winter
  // top-up agree: both ask the same question — how many of each trait does a
  // roster this size want — and get the same answer.
  const wanted = traitSlots(Math.round(share * eligibleDrivers.length), pool, held);
  for (let i = wanted.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [wanted[i], wanted[j]] = [wanted[j], wanted[i]];
  }

  for (let i = 0; i < Math.min(order.length, wanted.length); i++) byDriver.set(order[i], wanted[i]);
  return result;
}

/**
 * The exact list of trait codes a roster of this size should be carrying —
 * `spreadEvenly`, with unique traits capped at one and any already spoken for
 * dropped entirely.
 *
 * A UNIQUE CAP HANDS ITS SURPLUS BACK. This used to read the even spread once
 * and give a unique trait `1` outright, which was wrong in both directions and
 * only became visible the day FN — the one unique trait — was wired up:
 *
 *   • at a share of ZERO it dealt a Finisher anyway, because `1` ignored the
 *     target completely. A league with traits switched off had one.
 *   • at any real share it QUIETLY UNDERSHOT. Ask for 11 traits across 8 codes
 *     and the even spread wants 2 Finishers; FN can only take 1, so the league
 *     ended up with 10. D-EZ-3 calibrated 0.20 by measurement, and a deal that
 *     silently returns 0.18 makes that calibration a fiction.
 *
 * So the spread is now run in rounds: spread over everything, pin whatever
 * overflowed AT its cap, take it out of the pool and re-spread the rest. The
 * share is honoured exactly, right up to the point where the pool genuinely
 * cannot absorb it — 53 drivers all wanting a trait when one of the eight may
 * only ever be held by one driver.
 */
function traitSlots(
  total: number,
  pool: readonly TraitCode[],
  held: ReadonlySet<TraitCode>,
): TraitCode[] {
  const capOf = (code: TraitCode): number => {
    const def = TRAITS_BY_CODE[code];
    if (!def.unique) return Number.POSITIVE_INFINITY;
    return held.has(code) ? 0 : 1;
  };

  const counts = new Map<TraitCode, number>(pool.map((c) => [c, 0]));
  let remaining = Math.max(0, total);
  // Pool order is EZRA's printed order and is never shuffled here, so every
  // pass is deterministic and no die is spent.
  let open = pool.filter((c) => capOf(c) > 0);

  // Spread over everything that can still take a trait; if that hands a capped
  // code more than it may hold, pin it AT its cap, take it out of the pool and
  // spread what is left over the rest. Repeat until nothing overflows.
  //
  // Doing it this way rather than "place what fits, then dole out the leftover"
  // matters: the leftover pass always starts at the head of the printed order,
  // so the first two codes would collect every spare chip and the spread the
  // whole function exists to produce would tilt toward AW and DS.
  for (;;) {
    if (remaining <= 0 || open.length === 0) break;
    const targets = spreadEvenly(remaining, open);
    const overflow = open.filter((c) => (targets.get(c) ?? 0) > capOf(c));
    if (overflow.length === 0) {
      for (const code of open) counts.set(code, targets.get(code) ?? 0);
      remaining = 0;
      break;
    }
    for (const code of overflow) {
      counts.set(code, capOf(code));
      remaining -= capOf(code);
    }
    open = open.filter((c) => !overflow.includes(c));
  }

  const slots: TraitCode[] = [];
  for (const code of pool) {
    for (let i = 0; i < (counts.get(code) ?? 0); i++) slots.push(code);
  }
  return slots;
}

/**
 * Deal traits to drivers who have just become eligible, topping the league back
 * up to its share. Returns ONLY the new awards; existing traits are untouched.
 *
 * WHY THIS EXISTS. Without it the trait population decays. Drivers die, retire
 * and get demoted every winter, and their replacements are first-year rookies
 * who — per EZRA — carry nothing. Nothing ever adds a trait back, so over a long
 * career the DS population drains toward zero and super-speedway drafting quietly
 * stops happening. Nobody would ever see a bug report for that; the races would
 * just get duller, one season at a time, which is the worst kind of fault this
 * project can ship.
 *
 * WHO IS ELIGIBLE. A driver with at least one completed season (so not a
 * first-year rookie, `CITE_NO_ROOKIE_TRAITS`) who does not already carry a trait
 * (`CITE_ONE_TRAIT`). Drivers who already have one keep it — a trait is a
 * standing ability, and EZRA never takes one away.
 */
export function topUpTraits(
  seed: number,
  cards: readonly Pick<DriverCard, 'driverId' | 'trait'>[],
  options: TraitAssignmentOptions = {},
): TraitAssignment {
  const share = options.share ?? DEFAULT_TRAIT_SHARE;
  const rookies = new Set(options.rookieIds ?? []);
  const held = new Set<TraitCode>([
    ...(options.alreadyHeld ?? []),
    ...cards.map((c) => c.trait).filter((t): t is TraitCode => Boolean(t)),
  ]);

  const pool = (options.pool ?? IMPLEMENTED_TRAITS).filter((code) => TRAITS_BY_CODE[code]);
  const candidates = cards.filter((c) => !c.trait && !rookies.has(c.driverId));
  const empty: TraitAssignment = {
    byDriver: new Map(),
    eligible: candidates.length,
    rookiesSkipped: cards.filter((c) => rookies.has(c.driverId)).length,
    share,
    ruleCitation: `${CITE_ONE_TRAIT} ${CITE_NO_ROOKIE_TRAITS} ${CITE_TRAIT_SHARE}`,
  };
  if (pool.length === 0 || candidates.length === 0) return empty;

  // PER TRAIT, NOT IN AGGREGATE.
  //
  // Topping the league up to a total headcount and dealing whatever came next
  // off a shuffled deck looks equivalent and is not. Measured over ten seasons
  // it let INDIVIDUAL traits wander: the total sat on 11 every year while DS
  // drifted 2 → 1 → 0, and a season with no DS driver is a season where
  // super-speedway drafting silently does not exist. Nobody would file that as a
  // bug; the racing would just go flat. So each trait gets its own target and
  // its own deficit, and DS is refilled because DS is short — not because the
  // league happens to be one trait light overall.
  const heldCounts = new Map<TraitCode, number>();
  for (const c of cards) if (c.trait) heldCounts.set(c.trait, (heldCounts.get(c.trait) ?? 0) + 1);

  // The same slot list an untouched league of this size would hold, minus what
  // is already out there. Asking per trait is the whole point: a top-up that
  // only counts heads will hand a league that has lost every DS driver three
  // more of something else, and drafting silently stops.
  const slots = traitSlots(Math.round(share * cards.length), pool, new Set());
  const target = new Map<TraitCode, number>();
  for (const code of slots) target.set(code, (target.get(code) ?? 0) + 1);

  const wanted: TraitCode[] = [];
  for (const [code, want] of target) {
    const have = (heldCounts.get(code) ?? 0) + (TRAITS_BY_CODE[code].unique && held.has(code) ? 1 : 0);
    for (let i = have; i < want; i++) wanted.push(code);
  }
  if (wanted.length === 0) return empty;

  const rng = makeRng((seed ^ 0xc2b2ae35) >>> 0);
  const order = candidates.map((c) => c.driverId);
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  for (let i = wanted.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [wanted[i], wanted[j]] = [wanted[j], wanted[i]];
  }

  const byDriver = new Map<string, TraitCode>();
  for (let i = 0; i < Math.min(order.length, wanted.length); i++) byDriver.set(order[i], wanted[i]);
  return { ...empty, byDriver };
}

/**
 * Split `total` across `codes` as evenly as the integers allow, largest
 * remainder first, so 11 traits over 4 kinds comes out 3/3/3/2 rather than
 * 2/2/2/2 with three unallocated.
 */
function spreadEvenly(total: number, codes: readonly TraitCode[]): Map<TraitCode, number> {
  const out = new Map<TraitCode, number>();
  if (codes.length === 0) return out;
  const exact = total / codes.length;
  let allocated = 0;
  for (const code of codes) {
    const floor = Math.floor(exact);
    out.set(code, floor);
    allocated += floor;
  }
  // Hand the remainder out in a fixed order — the codes are already in EZRA's
  // printed order, so this is deterministic and needs no die.
  for (let i = 0; allocated < total; i++, allocated++) {
    const code = codes[i % codes.length];
    out.set(code, (out.get(code) ?? 0) + 1);
  }
  return out;
}

/** Apply an assignment to a set of cards, returning new cards. */
export function applyTraits<T extends DriverCard>(cards: readonly T[], assignment: TraitAssignment): T[] {
  return cards.map((card) => {
    const trait = assignment.byDriver.get(card.driverId);
    return trait ? { ...card, trait } : card;
  });
}

/**
 * Every way a roster can violate EZRA's trait constraints. Empty means clean.
 * Used by the tests and safe to call on a loaded career.
 */
export function auditTraits(
  cards: readonly Pick<DriverCard, 'driverId' | 'trait'>[],
  rookieIds: Iterable<string> = [],
): string[] {
  const problems: string[] = [];
  const rookies = new Set(rookieIds);
  const uniqueSeen = new Map<TraitCode, string[]>();

  for (const card of cards) {
    if (!card.trait) continue;
    const def = TRAITS_BY_CODE[card.trait];
    if (!def) {
      problems.push(`${card.driverId} carries unknown trait "${card.trait}"`);
      continue;
    }
    if (!def.implemented) {
      problems.push(`${card.driverId} carries ${def.code}, which the engine does not honour`);
    }
    if (rookies.has(card.driverId)) {
      problems.push(`${card.driverId} is a first-year rookie and may not carry ${def.code}`);
    }
    if (def.unique) {
      uniqueSeen.set(def.code, [...(uniqueSeen.get(def.code) ?? []), card.driverId]);
    }
  }

  for (const [code, ids] of uniqueSeen) {
    if (ids.length > 1) {
      problems.push(`${code} is awarded to only one driver, but ${ids.length} carry it: ${ids.join(', ')}`);
    }
  }
  return problems;
}
